import { Avatar, Col, Input, Tooltip } from "antd-v5";
import { find, isEmpty, isNull, map, uniqBy } from "lodash";
import React, { ReactNode, useCallback, useEffect, useState } from "react";
import UserAPI from "restAPI/UserAPI";
import { CellRenderers, Sorters } from "utils/tableUtils";
import { HistoryResponse } from "views/Inventory/Assets/AssetDetails/asset-history";

import ACDropdown from "ac-components/ACDropdown";
import ACBadge from "ac-components/ac-badge/ACBadge";
import ACButton from "ac-components/ac-button/ACButton";
import CheckIcon from "ac-components/ac-icons/CheckIcon";
import EditIcon from "ac-components/ac-icons/EditIcon";
import FilterIcon from "ac-components/ac-icons/FilterIcon";
import SearchIcon from "ac-components/ac-icons/SearchIcon";

import "./ac-history.less";

const ACHistoryEvent = ({
  renderHistory,
  history,
  eventDisplay,
}: {
  renderHistory: (item: HistoryResponse) => ReactNode;
  history: HistoryResponse[];
  eventDisplay: { [key: string]: string };
}) => {
  const [users, setUsers] = useState<
    Array<{ email: string; displayName: string }>
  >([]);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState(history);
  const [selectedValue, setSelectedValue] = useState<string[]>([]);

  useEffect(() => {
    const { promise, cancel } = UserAPI.getAll();
    promise.then((data) => setUsers(data));
    return cancel;
  }, []);

  const onHistorySearch = useCallback((e) => {
    setSearchText(e.target.value || "");
  }, []);

  const searchMethod = (history: HistoryResponse[], value: string) => {
    value = value.trim().toLowerCase();
    if (value.length === 0) {
      return history;
    }
    return history.filter((item: HistoryResponse) => {
      const label = {
        true: "active",
        false: "inactive",
      };
      const itemOldEventData =
        label[item.eventOldData as keyof typeof label] ?? item.eventOldData;
      const itemNewEventData =
        label[item.eventNewData as keyof typeof label] ?? item.eventNewData;
      return (
        eventDisplay[item.event].toLowerCase()?.includes(value) ||
        itemOldEventData?.toLowerCase()?.includes(value) ||
        itemNewEventData?.toLowerCase()?.includes(value)
      );
    });
  };

  const historyFilteredItems = uniqBy(
    history?.map((item) => {
      return {
        label: eventDisplay[item.event],
        value: eventDisplay[item.event],
      };
    }),
    "value",
  ).sort(Sorters.STRING("value"));

  const onFilterOptionClick = (value: any) => {
    setSelectedValue((prev) => {
      const updatedValues = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];
      const newFilteredData =
        updatedValues.length > 0
          ? history.filter((item) =>
              updatedValues.includes(eventDisplay[item.event]),
            )
          : history;
      setFilteredData(newFilteredData);
      return updatedValues;
    });
  };
  const filteredHistory = searchMethod(filteredData, searchText);
  return (
    <>
      <div className="d-flex justify-content-between align-items-center m-b-l">
        <div>
          <Input
            autoFocus
            prefix={<SearchIcon />}
            onChange={onHistorySearch}
            allowClear
            placeholder="Search history"
          />
        </div>
        <ACDropdown
          items={historyFilteredItems.map((item) => ({
            ...item,
            secondaryIcon: selectedValue.includes(item.value) ? (
              <CheckIcon />
            ) : (
              ""
            ),
          }))}
          onOptionClick={onFilterOptionClick}
          placement="bottomRight"
        >
          <ACButton type="outline" disabled={isEmpty(history)}>
            <FilterIcon />
            {!isEmpty(selectedValue) ? (
              <ACBadge type="beta" content={selectedValue.length} />
            ) : null}
          </ACButton>
        </ACDropdown>
      </div>
      {!isEmpty(filteredHistory) ? (
        map(filteredHistory, (item, index) => {
          const { createdBy, createdAt, event } = item;
          let userName: string;
          userName = isNull(createdBy)
            ? "SYSTEM"
            : find(users, { email: createdBy })?.displayName || createdBy;

          const dateTime = (
            <Tooltip title={CellRenderers.TIME(createdAt)} placement="right">
              {CellRenderers.TIME_RELATIVE(createdAt)}
            </Tooltip>
          );
          return (
            <Col key={index} style={{ padding: "16px 0" }}>
              <div className="d-flex align-items-center">
                <Avatar
                  size={30}
                  style={{ fontSize: 16, backgroundColor: "#6071F3" }}
                >
                  <EditIcon />
                </Avatar>

                <span style={{ marginLeft: 12, fontSize: 14, fontWeight: 600 }}>
                  {eventDisplay[event]} <span className="text-muted">by</span>{" "}
                  <Tooltip title={createdBy}> {userName} </Tooltip>
                </span>

                <div style={{ marginLeft: "auto", fontSize: 12 }}>
                  {dateTime}
                </div>
              </div>
              <div className="m-t-xs" style={{ paddingLeft: 44 }}>
                <div className="ac-history-details">{renderHistory(item)}</div>
              </div>
            </Col>
          );
        })
      ) : (
        <div className="text-center p-l">No history</div>
      )}
    </>
  );
};

export default ACHistoryEvent;
